/* eslint-disable no-useless-escape */

import {
  forwardRef,
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { InputMask } from "@react-input/mask";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useAppSelector } from "store/rootReducer";

import { HearingCustomFields } from "features/forms/HearingCustomFields";

import {
  Container,
  ContainerBody,
  ContainerInputs,
} from "pages/app/StyleCSS/bodyCreateEdit";

import { CheckBoxConfidential } from "components/CheckBoxConfidetial";
import DateInput from "components/DateInput";
import { DropdownGlobal } from "components/DropdownGlobal";
import { InputGeneral } from "components/Input";
import Multiline from "components/Multiline";

import { IHearingCustomField } from "services/hearingCustomField";
import { HearingRoleService } from "services/hearingRole";

import { applyMask, buildMaskConfig } from "utils/mask";

import { getRecordModeList } from "../constants";

const MaskedInputGeneral = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof InputGeneral>
>((inputProps, ref) => <InputGeneral {...inputProps} inputRef={ref} />);

MaskedInputGeneral.displayName = "MaskedInputGeneral";

interface ICreateProcedure {
  errorFields: {
    nameAudience: boolean;
    hours: boolean;
    date: boolean;
    audienceType: boolean;
    recordMode: boolean;
    procedureCustomerSource: boolean;
  };
  onChangeName: (e: string) => unknown;
  onChangeDate: (e: string) => unknown;
  date: any;
  isClassified: boolean;
  setIsClassified: (isClassified: boolean) => unknown;
  setProcedureTypeId: (e: number) => unknown;
  procedureNumber?: string;
  editTypeSelected?: any;
  setObjectType?: any;
  audience?: any;
  createAudienceMask: string;
  isEdit: boolean;
  recordModeSelected?: any;
  setRecordMode: (e: string) => unknown;
  audienceStatus?: string;
  onChangeDescription: (e: string) => unknown;
  audienceDescription?: string;
  customFields?: IHearingCustomField[];
  customFieldValues?: Record<number, string | boolean>;
  showCustomFieldErrors?: boolean;
  onCustomFieldChange?: (fieldId: number, value: string | boolean) => void;
  procedureCustomerSources?: Array<{ id: number | string; name: string }>;
  procedureCustomerSourceSelected?: any;
  setProcedureCustomerSource?: (source: any) => unknown;
}

const CreateProcedure = (props: ICreateProcedure) => {
  const { t } = useTranslation();
  const [audienceTypeList, setAudienceTypeList] = useState<any>([]);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const automaticDeadlineValuesRef = useRef<Record<number, string>>({});
  const { user } = useAppSelector((state) => state.user);

  const {
    audience,
    audienceDescription,
    createAudienceMask,
    customFieldValues,
    customFields,
    onChangeDescription,
    onChangeName,
    onCustomFieldChange,
    procedureCustomerSourceSelected,
    procedureCustomerSources,
    procedureNumber,
    setObjectType,
    setProcedureCustomerSource,
  } = props;

  const { configurations } = useSelector((store: any) => store);

  const customerProcedureDeadline = useMemo(() => {
    return configurations?.globalConfigurations?.find(
      (config: any) => config.name === "CustomerProcedureDeadline",
    )?.value;
  }, [configurations]);

  const procedureDeadlineDays = useMemo(() => {
    const deadlineDays = Number(
      String(customerProcedureDeadline ?? "").replace(/\D/g, ""),
    );

    return Number.isFinite(deadlineDays) && deadlineDays > 0 ? deadlineDays : 0;
  }, [customerProcedureDeadline]);

  const disabledRecordMode = useMemo(() => {
    return props.isEdit && props.audienceStatus === "E";
  }, [props.audienceStatus, props.isEdit]);

  const maskConfig = useMemo(
    () => buildMaskConfig(createAudienceMask),
    [createAudienceMask],
  );

  const linkedProcedureFields = useMemo(() => {
    if (!customFields) return [];

    return customFields
      .filter((field) => field.linkedProperty === "ProcedureNumber")
      .sort((a, b) => a.index - b.index);
  }, [customFields]);

  const procedureDeadlineFields = useMemo(() => {
    if (!customFields) return [];

    const dateFields = customFields.filter(
      (field) =>
        field.component === "DateInput" ||
        field.type === "date" ||
        field.type === "datetime",
    );
    const linkedDeadlineFields = dateFields.filter(
      (field) => field.linkedProperty === "ProcedureFinishDate",
    );

    return linkedDeadlineFields.length > 0 ? linkedDeadlineFields : dateFields;
  }, [customFields]);

  const todayDate = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const maxProcedureDeadlineDate = useMemo(() => {
    if (!procedureDeadlineDays) return null;

    const baseDate = props.date ? dayjs(props.date) : dayjs();
    if (!baseDate.isValid()) return null;

    return baseDate.add(procedureDeadlineDays, "day").format("YYYY-MM-DD");
  }, [procedureDeadlineDays, props.date]);

  const procedureDeadlineDateLimits = useMemo(() => {
    if (procedureDeadlineFields.length === 0) return undefined;

    return procedureDeadlineFields.reduce<
      Record<number, { minDate: string; maxDate?: string | null }>
    >((limits, field) => {
      limits[field.id] = {
        minDate: todayDate,
        maxDate: maxProcedureDeadlineDate,
      };

      return limits;
    }, {});
  }, [maxProcedureDeadlineDate, procedureDeadlineFields, todayDate]);

  const hasProcedureNumberLinked = linkedProcedureFields.length > 0;

  useEffect(() => {
    (async () => {
      if (user) {
        const response = await HearingRoleService.getAll(user?.customerId, 1);

        const getHearingType = response?.data?.data?.find(
          (type: any) => type?.id === audience?.hearingTypeId,
        );

        setObjectType?.(getHearingType);
        const onlyActives = response?.data?.data?.filter((e: any) => e.active);
        setAudienceTypeList(onlyActives);
      }
    })();
  }, [audience, setObjectType, user]);

  useEffect(() => {
    if (hasProcedureNumberLinked) {
      const timer = setTimeout(() => {
        descriptionRef.current?.focus();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [hasProcedureNumberLinked]);

  useEffect(() => {
    if (!hasProcedureNumberLinked || !customFieldValues) return;

    const values = linkedProcedureFields.map((field) => {
      const value = customFieldValues?.[field.id];
      if (value === undefined || value === "") return "";

      const digitsOnly = String(value).replace(/\D/g, "");
      if (!digitsOnly) return "";

      const size = Math.max(0, field.size || 0);
      if (!size) return digitsOnly;

      const normalized =
        digitsOnly.length > size ? digitsOnly.slice(0, size) : digitsOnly;

      return normalized.padStart(size, "0");
    });

    const allEmpty = values.every((value) => value === "");
    if (allEmpty) return;

    const composedValue = values.join("");
    const formattedValue = createAudienceMask
      ? applyMask(composedValue, createAudienceMask)
      : composedValue;

    if (formattedValue !== procedureNumber) {
      onChangeName(formattedValue);
    }
  }, [
    createAudienceMask,
    customFieldValues,
    hasProcedureNumberLinked,
    linkedProcedureFields,
    onChangeName,
    procedureNumber,
  ]);

  const getSelected = (typeObject: any) => {
    props.setProcedureTypeId(typeObject?.id);
    props.setObjectType?.(typeObject);
  };

  const getSelectedRecordMode = (typeObject: any) => {
    props.setRecordMode(typeObject?.id || "");
  };

  const getSelectedProcedureCustomerSource = (typeObject: any) => {
    setProcedureCustomerSource?.(typeObject);
  };

  const getProcedureDeadlineDate = (dateValue: string | null) => {
    if (!dateValue || !procedureDeadlineDays) return "";

    const selectedDate = dayjs(dateValue);
    if (!selectedDate.isValid()) return "";

    return selectedDate.add(procedureDeadlineDays, "day").format("YYYY-MM-DD");
  };

  const handleProcedureDateChange = (value: string | null) => {
    props.onChangeDate(value || "");

    if (!onCustomFieldChange || procedureDeadlineFields.length === 0) return;

    const deadlineDate = getProcedureDeadlineDate(value);
    if (!deadlineDate) return;

    procedureDeadlineFields.forEach((field) => {
      const currentValue = customFieldValues?.[field.id];
      const previousAutomaticValue =
        automaticDeadlineValuesRef.current[field.id];
      const shouldApplyDefault =
        currentValue === undefined ||
        currentValue === "" ||
        currentValue === previousAutomaticValue;

      if (!shouldApplyDefault) return;

      automaticDeadlineValuesRef.current[field.id] = deadlineDate;
      onCustomFieldChange(field.id, deadlineDate);
    });
  };

  const onChangeProcessValueDefault = (value: string) => {
    const partner = /[\d\.\/\-\'']/gi;

    if (!!partner.test(value[value.length - 1]) || value === "") {
      return onChangeName(value);
    }
  };

  const recordModeList = getRecordModeList();
  const selectedRecordMode = recordModeList.find(
    (recordMode) => Number(recordMode.id) === Number(props.recordModeSelected),
  );
  const procedureCustomerSourceDropdownKey = `procedure-customer-source-${
    typeof procedureCustomerSourceSelected === "object" &&
    procedureCustomerSourceSelected
      ? `${String(procedureCustomerSourceSelected.id)}-${String(procedureCustomerSourceSelected.name)}`
      : String(procedureCustomerSourceSelected || "empty")
  }-${String(procedureCustomerSources?.length || 0)}`;

  return (
    <Container>
      <ContainerBody>
        <ContainerInputs>
          {props.createAudienceMask ? (
            <InputMask
              component={MaskedInputGeneral}
              mask={maskConfig.mask}
              showMask
              replacement={maskConfig.replacement}
              error={props.errorFields.nameAudience}
              value={procedureNumber || ""}
              onChange={onChangeName}
              label={t("audiences.procedureNumber")}
              maxLength={200}
              required={true}
              nativeRequired={false}
              disabled={hasProcedureNumberLinked}
              onKeyPress={(e) =>
                e.key === "Enter" ? e.preventDefault() : null
              }
              helperText={
                createAudienceMask && maskConfig.expectedLength
                  ? t("audiences.procedureNumberHelper", {
                      length: maskConfig.expectedLength,
                    })
                  : undefined
              }
            />
          ) : (
            <InputGeneral
              error={props.errorFields.nameAudience}
              value={procedureNumber || ""}
              onChange={onChangeProcessValueDefault}
              label={t("audiences.procedureNumber")}
              maxLength={200}
              required={true}
              nativeRequired={false}
              disabled={hasProcedureNumberLinked}
              onKeyPress={(e) =>
                e.key === "Enter" ? e.preventDefault() : null
              }
            />
          )}
        </ContainerInputs>

        <ContainerInputs fullWidth>
          <Multiline
            ref={descriptionRef}
            label={t("audiences.description")}
            value={audienceDescription || ""}
            onChange={(value: any) => onChangeDescription(value)}
            name={"descriptionAudience"}
            height="auto"
            maxRows={5}
            maxLength={250}
          />
        </ContainerInputs>

        <ContainerInputs>
          <DateInput
            label={t("audiences.dateTime")}
            setValue={handleProcedureDateChange}
            value={props.date}
            required={true}
            width={200}
            error={
              props.date === "Invalid Date" ||
              (props.errorFields.date && !props.date)
            }
          />
          {customFields &&
            customFields.length > 0 &&
            customFieldValues &&
            onCustomFieldChange && (
              <div style={{ width: "200px" }}>
                <HearingCustomFields
                  showErrors={props.showCustomFieldErrors}
                  fields={customFields}
                  values={customFieldValues}
                  onChange={onCustomFieldChange}
                  dateLimits={procedureDeadlineDateLimits}
                />
              </div>
            )}
        </ContainerInputs>

        <ContainerInputs>
          <DropdownGlobal
            withoutMarginBottom
            width="100%"
            error={props.errorFields.audienceType}
            editDropdown={props.editTypeSelected}
            labelName={t("labels.types")}
            options={audienceTypeList}
            required={true}
            setSelected={getSelected}
            wantAnObject={true}
          />
        </ContainerInputs>

        <ContainerInputs>
          <DropdownGlobal
            key={procedureCustomerSourceDropdownKey}
            withoutMarginBottom
            width="100%"
            error={props.errorFields.procedureCustomerSource}
            editDropdown={procedureCustomerSourceSelected}
            labelName={t("common.origin")}
            options={procedureCustomerSources || []}
            required={true}
            setSelected={getSelectedProcedureCustomerSource}
            wantAnObject={true}
          />
        </ContainerInputs>

        <ContainerInputs>
          <DropdownGlobal
            withoutMarginBottom
            width="100%"
            error={props.errorFields.recordMode}
            editDropdown={selectedRecordMode}
            labelName={t("audiences.recordMode")}
            options={recordModeList}
            required={true}
            setSelected={getSelectedRecordMode}
            wantAnObject={true}
            disabled={disabledRecordMode}
          />
        </ContainerInputs>

        <CheckBoxConfidential
          label={t("audiences.confidential")}
          value={props.isClassified}
          onChange={props.setIsClassified}
        />
      </ContainerBody>
    </Container>
  );
};

export default CreateProcedure;
